import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import {
  Drawer,
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  Link,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  format,
  parseISO,
  isBefore,
  differenceInDays,
} from "date-fns";

import {
  closeInvoiceDrawer,
  deleteLineItemFile,
} from "../../../../redux/slices/invoicesSlice";
import LineItemFileUploadDialog from "./LineItemFileUploadDialog";
import { apiGet } from "../../../../services/api/apiClient";

const INVOICE_STATUS_WORD: Record<string, string> = {
  "0": "paid",
  "1": "open",
  "2": "partial",
};

const InvoiceDetailsDrawer = ({ maxWidth }: any) => {
  const dispatch = useAppDispatch();
  const { selectedInvoice, invoiceAttachments, deleteFileStatus } = useAppSelector(
    (state) => state.invoices
  ) as any;
  const currentUser = useAppSelector((state: any) => state.login?.user);
  const isAdmin = ["admin", "super-admin"].includes(currentUser?.role);

  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [eventsStatus, setEventsStatus] = useState<"idle" | "loading" | "ready">("idle");
  const [tabValue, setTabValue] = useState(0);

  const [uploadDialog, setUploadDialog] = useState<{
    open: boolean;
    lineItemId: string;
    description: string;
  }>({ open: false, lineItemId: "", description: "" });

  const handleClose = () => {
    dispatch(closeInvoiceDrawer());
  };

  const handleOpenUpload = (lineItemId: string, description: string) => {
    setUploadDialog({ open: true, lineItemId, description });
  };

  const handleCloseUpload = () => {
    setUploadDialog({ open: false, lineItemId: "", description: "" });
  };

  const handleDeleteFile = (fileId: string) => {
    setDeletingFileId(fileId);
    dispatch(deleteLineItemFile(fileId));
  };

  // Clear deleting state when delete operation completes
  useEffect(() => {
    if (deleteFileStatus !== "loading") {
      setDeletingFileId(null);
    }
  }, [deleteFileStatus]);

  useEffect(() => {
    if (!isAdmin || tabValue !== 2) return;
    const providerId = selectedInvoice?.providerId || selectedInvoice?.id;
    if (!providerId) return;
    let cancelled = false;
    setEventsStatus("loading");
    apiGet({
      endpoint: "/get-invoice-events",
      params: { providerId },
      withAuth: true,
    })
      .then((data: any) => {
        if (!cancelled) {
          setEvents(Array.isArray(data) ? data : []);
          setEventsStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEvents([]);
          setEventsStatus("ready");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, tabValue, selectedInvoice?.providerId, selectedInvoice?.id]);

  const getFilesForLineItem = (lineItemId: string) => {
    return (invoiceAttachments || []).filter(
      (a: any) => a.lineItemId === lineItemId,
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // const handleChargeInvoice = () => {
  //   dispatch(chargeInvoice({ invoiceId: selectedInvoice?.id }));
  // };

  const dueDate = selectedInvoice?.dueDate
    ? parseISO(selectedInvoice.dueDate)
    : null;
  const isPastDue =
    selectedInvoice?.paymentStatus === "1" &&
    dueDate &&
    isBefore(dueDate, new Date());

  const daysPastDue = dueDate ? differenceInDays(new Date(), dueDate) : 0;

  const status = {
    0: "Paid in full",
    1: "Open",
    2: "Partial payment",
    4: "Scheduled",
  };

  const paymentStatus = (status as any)[selectedInvoice?.paymentStatus] || "Unknown";

  const color = {
    0: "success",
    1: "warning",
    2: "info",
    4: "secondary",
  };

  const paymentStatusColor = (color as any)[selectedInvoice?.paymentStatus] || "default";

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Drawer
      open={!!selectedInvoice}
      onClose={handleClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(19, 21, 23, 0.45)",
        },
        "& .MuiDrawer-paper": {
          mt: 7,
          backgroundColor: "var(--surface-container-lowest, #ffffff)",
          height: "calc(100% - 56px)",
          borderRadius: 0,
          border: "1px solid var(--outline-variant, #dcdcdc)",
          width: "100%",
          maxWidth: `${maxWidth ? maxWidth : 500}px`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
        },
      }}
      anchor="right"
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid var(--outline-variant, #dcdcdc)",
        }}
      >
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Invoice {selectedInvoice?.invoiceNumber} Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 2.5, sm: 3 } }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="invoice details tabs"
        >
          <Tab label="Info" />
          <Tab
            label={"Line Items"}
            disabled={
              !selectedInvoice?.invoiceLineItems?.length
            }
          />
          {isAdmin && <Tab label="History" />}
        </Tabs>
      </Box>
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          flexGrow: 1,
          overflowY: "auto",
        }}
      >
        <Grid
          container
          sx={{
            alignContent: "space-between",
          }}
        >
          {tabValue === 0 && (
            <Grid size={24}>
              <Grid container rowSpacing={1.75}>
                {isPastDue && (
                  <Grid size={12}>
                    <Alert variant="filled" severity="error">
                      Past due {daysPastDue} days
                    </Alert>
                  </Grid>
                )}
                <Grid size={12}>
                  <Typography variant="subtitle1">Invoice Date:</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography>
                    {selectedInvoice?.invoiceDate
                      ? format(
                          parseISO(selectedInvoice.invoiceDate),
                          "MM/dd/yyyy"
                        )
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle1">Due Date:</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography
                    style={{
                      color: isPastDue ? "red" : "inherit",
                      fontWeight: isPastDue ? "bold" : "normal",
                    }}
                  >
                    {selectedInvoice?.dueDate
                      ? format(parseISO(selectedInvoice.dueDate), "MM/dd/yyyy")
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle1">Amount:</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography>
                    {selectedInvoice?.amount?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle1">Payment Status:</Typography>
                </Grid>
                <Grid size={12}>
                  <Chip label={paymentStatus} color={paymentStatusColor} />
                </Grid>
                <Grid size={12}>
                  <Typography variant="subtitle1">Description:</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography>{selectedInvoice?.description}</Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
          {tabValue === 1 && (
            <Grid size={24}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                Line Items
              </Typography>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 0,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Files</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice?.invoiceLineItems?.map(
                      (item: any, index: number) => {
                        const lineItemId = item.id || String(index);
                        const lineFiles = getFilesForLineItem(lineItemId);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              {item.description || "—"}
                            </TableCell>
                            <TableCell align="right">
                              {item.quantity != null
                                ? Number(item.quantity).toLocaleString(
                                    "en-US"
                                  )
                                : "—"}
                            </TableCell>
                            <TableCell align="right">
                              {item.price != null
                                ? Number(item.price).toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                    }
                                  )
                                : "—"}
                            </TableCell>
                            <TableCell align="right">
                              {item.amount != null
                                ? Number(item.amount).toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                    }
                                  )
                                : "—"}
                            </TableCell>
                            <TableCell align="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                {isAdmin && (
                                  <Tooltip title="Attach files">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        handleOpenUpload(
                                          lineItemId,
                                          item.description || "",
                                        )
                                      }
                                    >
                                      <AttachFileIcon
                                        fontSize="small"
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {lineFiles.length > 0 && (
                                  <Chip
                                    label={lineFiles.length}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                              {lineFiles.length > 0 && (
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                  }}
                                >
                                  {lineFiles.map((file: any) => (
                                    <Box
                                      key={file.id}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Link
                                        href={file.accessUrl || file.fileUrl}
                                        target="_blank"
                                        rel="noopener"
                                        underline="hover"
                                        sx={{
                                          fontSize: "0.75rem",
                                          flexGrow: 1,
                                          overflow: "hidden",
                                          textOverflow:
                                            "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: 120,
                                          display: "inline-block",
                                        }}
                                      >
                                        {file.fileName}
                                      </Link>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          fontSize: "0.65rem",
                                        }}
                                      >
                                        {formatFileSize(
                                          file.fileSize,
                                        )}
                                      </Typography>
                                      {isAdmin && (
                                        deletingFileId === file.id ? (
                                          <CircularProgress
                                            size={14}
                                            sx={{ mx: 0.25 }}
                                          />
                                        ) : (
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              handleDeleteFile(
                                                file.id,
                                              )
                                            }
                                            sx={{ p: 0.25 }}
                                          >
                                            <DeleteIcon
                                              fontSize="small"
                                              sx={{
                                                fontSize:
                                                  "0.875rem",
                                              }}
                                            />
                                          </IconButton>
                                        )
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {tabValue === 2 && isAdmin && (
            <Grid size={24}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                Invoice history
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Detail</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventsStatus === "loading" ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : events.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3}>
                          No history recorded for this invoice yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      events.map((event: any, index: number) => {
                        const data = event.data || {};
                        let detail = "";
                        if (event.type === "InvoiceStatusChanged") {
                          const from =
                            INVOICE_STATUS_WORD[data.fromStatus] ||
                            data.fromStatus ||
                            "unknown";
                          const to =
                            INVOICE_STATUS_WORD[data.toStatus] ||
                            data.toStatus ||
                            "unknown";
                          detail = `${from} → ${to} · balance ${Number(
                            data.balance || 0,
                          ).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}`;
                        } else {
                          const parts = [];
                          if (data.status) parts.push(data.status);
                          if (data.amount != null)
                            parts.push(
                              Number(data.amount).toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              }),
                            );
                          if (data.chargeId) parts.push(`charge ${data.chargeId}`);
                          if (data.error) parts.push(data.error);
                          detail = parts.join(" · ");
                        }
                        return (
                          <TableRow key={index}>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {event.date
                                ? format(parseISO(event.date), "MMM dd, yyyy HH:mm")
                                : "—"}
                            </TableCell>
                            <TableCell>{event.type}</TableCell>
                            <TableCell>{detail}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          <Grid size={24} sx={{ mt: 3 }}>
            <Button
              fullWidth
              color="success"
              variant="contained"
              // onClick={handleChargeInvoice}
            >
              Approve and open next Invoice
            </Button>
          </Grid>
        </Grid>
      </Box>

      <LineItemFileUploadDialog
        open={uploadDialog.open}
        onClose={handleCloseUpload}
        invoiceId={selectedInvoice?.id || ""}
        lineItemId={uploadDialog.lineItemId}
        lineItemDescription={uploadDialog.description}
        existingFiles={
          getFilesForLineItem(uploadDialog.lineItemId)
        }
      />
    </Drawer>
  );
};

export default InvoiceDetailsDrawer;
